import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { LoadingScreen, UnavailableScreen } from "../components/ui";
import { ApiRequestError } from "./api";
import { CalendarOperatorOnlyError, fetchCalendarBootstrap, type CalendarBootstrap } from "./native-calendar-api";
import { takeRead } from "./startup-reads";

type CalendarAccess = { enabled: false } | { enabled: true; bootstrap: CalendarBootstrap };
const NativeCalendarContext = createContext<CalendarAccess>({ enabled: false });
export function useNativeCalendar() { return useContext(NativeCalendarContext); }
export const NATIVE_CALENDAR_UI_ENABLED = import.meta.env.VITE_NATIVE_CALENDAR_ENABLED !== "false";

export function NativeCalendarProvider({ children }: { children: ReactNode }) {
  if (!NATIVE_CALENDAR_UI_ENABLED) return children;
  return <EnabledCalendarProvider>{children}</EnabledCalendarProvider>;
}

function EnabledCalendarProvider({ children }: { children: ReactNode }) {
  const [access, setAccess] = useState<CalendarAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let current = true;
    setAccess(null); setError(null);
    void (retry === 0 ? takeRead("calendar", fetchCalendarBootstrap) : fetchCalendarBootstrap()).then((bootstrap) => {
      if (current) setAccess({ enabled: true, bootstrap });
    }).catch((caught: unknown) => {
      if (!current) return;
      if (caught instanceof CalendarOperatorOnlyError || (caught instanceof ApiRequestError && caught.status === 404)) setAccess({ enabled: false });
      else setError(caught instanceof Error ? caught.message : "Не удалось проверить права календаря.");
    });
    return () => { current = false; };
  }, [retry]);
  if (error) return <UnavailableScreen title="Календарь недоступен" error={error} guard="Права доступа не подтверждены. Повторите проверку." onRetry={() => setRetry((value) => value + 1)} />;
  if (!access) return <LoadingScreen />;
  return <NativeCalendarContext.Provider value={access}>{children}</NativeCalendarContext.Provider>;
}
